// ═══════════════════════════════════════════════════════════════════
// ultiMotion.js — THE TWELVE SIGNATURES · H24 · Terror TCG
// One score per U'hlti. Twelve exist. They are allowed to be the
// loudest thing that ever happens on this board.
// ═══════════════════════════════════════════════════════════════════
//
// The blueprint's line about this card class, kept verbatim because
// every score below is built to satisfy it:
//
//     "It should feel like a war crime."
//
// ── WHAT EARNS THAT ────────────────────────────────────────────────
// A U'hlti is the only class that charges BOTH currencies. Show
// Stomper costs 10 Khao and 10 Anima — a third of a Warlock's starting
// Anima, spent to play one card. I or None costs the same and then
// makes both players dismantle their own boards. These are not big
// spells; they are the moments a duel is decided, and there are exactly
// twelve of them in the entire game.
//
// So the budget here is roughly double an Impera's, and every one of
// them opens on `signature()` — the pillar, the rising coil, the crown
// — before it does the thing that only it can do. That shared opening
// is what makes a U'hlti recognisable from across the room before the
// player has read a single word of it, and it is why the twelve can
// afford to diverge so wildly in their second halves.
//
// ── THE ONE RULE THAT KEPT THESE FROM BECOMING NOISE ───────────────
// Every score here has ONE image. Not four. Super Nova is a star
// dying; Sweet Dreams is a lullaby with a knife in it; Show Stomper is
// a foot coming down. When a score started acquiring a second idea, the
// second idea got cut — because at this length and this brightness,
// two images do not read as richness, they read as a mess, and the
// player's actual experience is "something enormous happened, I don't
// know what."
//
// The 8.5s HOLD_CAP in strikeLanguage is the outer bound and none of
// these come close to it. The longest is I or None at 2.4s. A U'hlti
// that ran five seconds would be a cutscene, and caP'n's standing T17
// note — "don't want people wasting their turn watching animations" —
// applies to the best animation in the game exactly as much as to the
// worst.
// ═══════════════════════════════════════════════════════════════════

import {
  P, score, signature, toll, wound, SHAKE,
  SURGE, slash, shock, motes, pillar, spiral,
  bolt, wave, bloom, stream, glyph, rune,
  veil, shatter, coin, crack, embers, lattice,
  halo, star, echoes, sever, plume
} from "./motionKit";

const S = { to: "self" };

export const ULTIS = {

// ═══════════════════════════════════════════════════════════════════
// SPACE
// ═══════════════════════════════════════════════════════════════════

  // ── H-01 · SUPER NOVA ─────────────────────────────────────────────
  // "Dim your adversary's Anima by 4." The card, the being and the
  // U'hlti all share one name — H-01 Super Nova's ultimate is literally
  // itself — and the line is named for the stages of a dying star:
  // Blue Giant → Quasar → Super Nova. There is exactly one image
  // available here and it would be perverse to reach for another.
  //
  // The structural trick is the PAUSE. The star collapses inward
  // first (motes running `in`, the spiral tightening) and then there is
  // a beat of nothing at ~620ms before the detonation. That silence is
  // what makes the blast land; a supernova that expands continuously
  // from frame one is a firework.
  u_h01_lord: score("Super Nova", [
    ...signature(P.starWhite, P.cyanHot),
    motes(180, P.starCore, { ...S, ms: 460, n: 44, r: 3.2, size: 0.08, tone: "in" }),
    // ── the collapse completes, and the board holds its breath ──
    star(700, P.starWhite, { ...S, ms: 520, r: 4.6, size: 0.09 }),
    bloom(720, P.starWhite, { ...S, ms: 420, r: 2.6, tone: 1 }),
    wave(760, P.cyanHot, { from: "self", to: "target", ms: 620, r: 7.0, size: 0.6, tone: 0.85 }),
    ...wound(P.starCore, 1080, 3),
    shock(1140, P.nebula, { ...S, ms: 660, size: 0.5, r: 5.2, tone: 0.35 }),
  ], 1900, { shake: SHAKE.heavy(700) , surge: SURGE.full(P.starWhite, 700) }),

  // ── H-09 · RADIANT SHOWER ─────────────────────────────────────────
  // "Dim the Lyf of your adversary's cards by 1, or their Anima by 2."
  // Self-proclaimed Generals of the stars, whose Lyt Attack is already
  // a disciplined three-round Blaster Volley. The U'hlti is what
  // happens when the General stops aiming: not a volley, a SHOWER —
  // ordnance falling across their entire half rather than at any one
  // thing. Which is exactly what "your adversary's cardS" means.
  //
  // Three pillars staggered across the far side do the work. They are
  // deliberately NOT aimed at the victim slot: the whole point is that
  // this one is not aimed at all.
  u_h09_lord: score("Radiant Shower", [
    ...signature(P.cyanHot, P.starCore),
    stream(320, P.starWhite, { from: "selfsky", to: "target", ms: 720, n: 70, r: 0.9, size: 0.07 }),
    pillar(520, P.cyanHot, { ms: 560, r: 5.4, size: 0.34, spin: 0.4 }),
    pillar(640, P.starCore, { ms: 560, r: 5.4, size: 0.3, spin: -0.4 }),
    pillar(760, P.starWhite, { ms: 560, r: 5.4, size: 0.38, spin: 0.5 }),
    ...wound(P.cyanHot, 900, 3),
  ], 1700, { surge: SURGE.soft(P.cyanHot, 520) }),

  // ── XULAN · SWEET DREAMS ──────────────────────────────────────────
  // "Dim your adversary's Anima by 1 whenever they Lyt Attack." A
  // standing curse, from a race of assassins that drift the Void in
  // search of revenge, and the Lord is "Hate reverting the Surgeons to
  // what they once were."
  //
  // The name is the whole direction and it is a threat wearing a
  // lullaby: this is the only U'hlti in the game that is QUIET. Soft
  // violet settles over the adversary's half, a ward closes there, and
  // the very last thing that happens — after the pretty part is over —
  // is one thin sever, unhurried, going nowhere in particular. Nothing
  // is damaged. It is simply established that it will be.
  u_xulan_lord: score("Sweet Dreams", [
    ...signature(P.violetPale, P.scalpel),
    // A lullaby SETTLES over their half. Motes converging said
    // "something arrives"; a plume says "something is already here and
    // is getting thicker," which is the actual threat this card makes.
    plume(340, P.violetPale, { ms: 820, n: 44, r: 2.4, size: 0.1, size2: 1.4, tone: 0.6 }),
    veil(520, P.shadow, { ms: 760, r: 2.2, size: 0.6 }),
    rune(700, P.violetIce, { ms: 720, r: 1.8, n: 14, spin: 0.25, tone: "close" }),
    sever(1180, P.scalpel, { ms: 560, r: 1.9, size: 0.026, size2: 0.24, tone: -8 }),
  ], 1780, { surge: SURGE.soft(P.violetPale, 520) }),

// ═══════════════════════════════════════════════════════════════════
// PHANTOM
// ═══════════════════════════════════════════════════════════════════

  // ── SPECTRAL TIGER · HAUNTING PRESENCE ────────────────────────────
  // Authored in H23 Ship 1 and reproduced UNCHANGED. caP'n has seen
  // this one and signed off on it; this file is now its home, not its
  // rewrite. "Dim the Lyf of your adversary's cards by 1 and their
  // Anima by 2." 4 Khao AND 6 Anima.
  u_spectral_tiger_lord: score("Haunting Presence", [
    pillar(0, P.phantom, { ...S, ms: 720, r: 6.2, size: 0.56, spin: 0.7 }),
    spiral(100, P.violetPale, { ...S, ms: 720, tone: "up", spin: 3, r: 0.82, size: 3.1 }),
    glyph(480, P.violetIce, { ...S, ms: 640, tone: "◈", size: 52, r: 0.85 }),
    wave(520, P.phantom, { from: "self", to: "target", ms: 680, r: 6.0, size: 0.62, tone: 0.85 }),
    shock(620, P.violetDeep, { ...S, ms: 560, size: 0.3, r: 3.6 }),
    motes(660, P.violetPale, { ms: 560, n: 44, r: 3.0, size: 0.08 }),
  ], 1400, { surge: SURGE.soft(P.phantom, 520) }),

  // ── SHADOW FOX · ROYAL DARKNESS ───────────────────────────────────
  // "Dim the Lyf of your adversary's cards by 2 and their Anima by 1."
  // "Rulers of Shadow Spheres... loyalty amongst only the most
  // malevolent of D'aeterni."
  //
  // The Fox's verb has always been `tone: "in"` — the dark converging
  // from everywhere at once, which is how Nine Shadows works. At U'hlti
  // scale that verb is turned on the ENTIRE far half of the table: the
  // adversary's side is not attacked, it is simply covered. And the
  // brightest thing in the score is the crown, at the end, on this
  // side. The Fox does not go over there. The dark does.
  u_shadow_fox_lord: score("Royal Darkness", [
    ...signature(P.violetDeep, P.amethyst),
    // The dark does not converge here, it FLOODS — the Fox stays home
    // and the darkness goes over there and settles on everything.
    // The plume is shortened from 860ms and the crown pulled forward,
    // because the linter counted 7 things live at 1120ms — the flood
    // was arriving at the same instant as the ward, the wound AND the
    // crown, and at that point the player is not seeing darkness fall,
    // they are seeing purple. The flood now finishes BEFORE the veil
    // closes over it, which is also the correct order for the story.
    plume(300, P.shadow, { ms: 620, n: 52, r: 3.4, size: 0.12, size2: 1.8, tone: 0.85 }),
    wave(560, P.violetDeep, { from: "self", to: "target", ms: 700, r: 6.6, size: 0.66, tone: 0.9 }),
    veil(900, P.shadow, { ms: 700, r: 2.4, size: 0.62 }),
    ...wound(P.amethyst, 940, 2),
    halo(1180, P.violetIce, { ...S, ms: 700, r: 1.25, size: 0.9, spin: 1.1 }),
  ], 1880, { surge: SURGE.full(P.violetDeep, 560) }),

  // ── INVERTED SERPENT · INVERTED FANGS ─────────────────────────────
  // "If this being's Lyt Attack dimmed Anima this turn, dim their Anima
  // by an additional 2." A follow-through, conditional on a bite that
  // has already happened — so this score opens mid-motion rather than
  // from rest. The coil is ALREADY closed when the card resolves.
  //
  // Two punctures, and they are the widest and slowest slash pair in
  // the game: the species' fangs, at Lord scale, arriving second.
  u_inverted_serpent_lord: score("Inverted Fangs", [
    ...signature(P.amethyst, P.violetIce),
    spiral(260, P.amethyst, { ms: 720, spin: 3.2, r: 1.0, size: 1.5 }),
    rune(420, P.violetDeep, { ms: 680, r: 1.7, n: 12, spin: 0.8, tone: "close" }),
    slash(880, P.violetIce, { ms: 640, n: 2, r: 1.9, tone: 7 }),
    ...wound(P.phantom, 900, 3),
  ], 1620, { surge: SURGE.soft(P.amethyst, 420) }),

// ═══════════════════════════════════════════════════════════════════
// TIME
// ═══════════════════════════════════════════════════════════════════

  // ── Z4Z · APOCALYPSE NOW ──────────────────────────────────────────
  // "This being gains +1 Lyt for every Time Being in combat." The only
  // U'hlti in the game that damages nothing — it is a pure sharpening,
  // and the score must therefore point entirely INWARD. Every other
  // Signature here eventually crosses the seam; this one never does,
  // and that is the read.
  //
  // Echoes running target → self are the other machines' time being
  // gathered into this one. Then the ground cracks under it, because
  // "these demons are responsible for destroying 1% of the Unstable
  // Worlds" and it is now holding all of that at once.
  u_z4z_lord: score("Apocalypse Now", [
    ...signature(P.time, P.rust),
    echoes(300, P.emberGold, { from: "target", to: "self", ms: 680, n: 5, size: 0.6, tone: 0.5 }),
    crack(560, P.rust, { ...S, ms: 760, n: 10, r: 3.2 }),
    bolt(720, P.time, { from: "selfsky", to: "self", ms: 280, r: 0.4 }),
    bolt(830, P.emberGold, { from: "selfsky", to: "self", ms: 240, r: 0.26 }),
    star(900, P.judgeGold, { ...S, ms: 460, r: 2.4, size: 0.07 }),
    ...toll(P.rust, 940, 3, "self"),
  ], 1720, { surge: SURGE.soft(P.time, 560) }),

  // ── DG-0 · JUSTICE REIGNS ─────────────────────────────────────────
  // "Send 1 Non-Being to Necropolis, or draw 1 card." The Verdict, at
  // Lord weight, and it keeps the Verdict's one structural signature:
  // it CLOSES before it strikes. The iris contracts onto the target
  // (a shock written with size > r, which the primitive reads as an
  // iris shutting), the seal is set, and only then does the light come
  // down and the sentence get carried out.
  //
  // The sever rather than a slash is deliberate and it is the rule
  // across every file: a slash is a wound, a sever is a sentence. This
  // card removes something from the game.
  u_dg0_lord: score("Justice Reigns", [
    ...signature(P.judgeGold, P.emberGold),
    shock(300, P.emberGold, { ms: 620, size: 3.4, r: 0.55, tone: 0.9 }),
    rune(520, P.judgeGold, { ms: 700, r: 1.7, n: 16, spin: 0.3 }),
    glyph(700, P.judgeGold, { ms: 560, tone: "⚖", size: 46, r: 0.6 }),
    pillar(880, P.judgeGold, { ms: 620, r: 5.6, size: 0.48, spin: 0.2 }),
    sever(1020, P.brass, { ms: 620, r: 2.1, size: 0.05, size2: 0.36, tone: -14 }),
    ...toll(P.emberGold, 1080, 3),
  ], 1760, { shake: SHAKE.hard(1020) , surge: SURGE.full(P.judgeGold, 880) }),

  // ── ANTDROID · KING OF THE HILL ───────────────────────────────────
  // "ALL of your beings' Lyt Attacks can be directed at your
  // adversary's Anima instead of a being." The word is ALL, and no
  // other U'hlti grants something to the whole board.
  //
  // So this is the only score in the game that fires the same primitive
  // from three different anchors: streams leaving `self`, `back` and
  // the seam, all converging on the enemy plate. Three columns, not
  // one. "It is an Ant's duty to conquer... until the final folding of
  // time, they will."
  u_antdroid_lord: score("King of the Hill", [
    ...signature(P.swarm, P.brass),
    lattice(280, P.brass, { ...S, ms: 700, r: 1.15, n: 12, size: 0.055 }),
    stream(560, P.time, { from: "self", to: "target", ms: 760, n: 52, r: 0.22, size: 0.06 }),
    stream(640, P.swarm, { from: "back", to: "target", ms: 760, n: 44, r: 0.3, size: 0.055 }),
    stream(720, P.brass, { from: "seam", to: "target", ms: 700, n: 40, r: 0.26, size: 0.055 }),
    ...wound(P.swarm, 1180, 3),
    halo(1240, P.emberGold, { ...S, ms: 700, r: 1.2, size: 0.88, spin: 1.4 }),
  ], 1940, { surge: SURGE.soft(P.swarm, 560) }),

// ═══════════════════════════════════════════════════════════════════
// DRAGON
// ═══════════════════════════════════════════════════════════════════

  // ── LUCKY DRAGON · DRAGON EYES ────────────────────────────────────
  // "Roll 2 dice, if their sum is equal to 2, then you've just won 2
  // Shadow Gamblings, and you can activate another U'hlti." Sum of two
  // is snake eyes — the longest odds in the game, 1 in 36, and the
  // payoff is another U'hlti. "The Greatest Ever, Vavixzu the
  // A'oeternis, was tricked by these Dragons."
  //
  // Two coins, thrown at slightly different heights so they do not land
  // together — the second landing after the first is the entire drama
  // of a two-dice roll and it costs one number to say. Nothing else
  // happens until both are down.
  u_lucky_dragon_lord: score("Dragon Eyes", [
    ...signature(P.swarm, P.jynxi),
    coin(300, P.swarm, { ...S, ms: 820, r: 2.3, size: 0.33, spin: 10 }),
    coin(380, P.jynxi, { ...S, ms: 860, r: 2.7, size: 0.33, spin: 12 }),
    glyph(1080, P.jynxi, { ...S, ms: 620, tone: "⚄", size: 54, spin: 2, r: 0.8 }),
    star(1140, P.emberGold, { ...S, ms: 440, r: 2.2, size: 0.06 }),
    ...toll(P.swarm, 1180, 3, "self"),
  ], 1900, { surge: SURGE.soft(P.jynxi, 1080) }),

  // ── PREMIER DRAGON · SHOW STOMPER ─────────────────────────────────
  // "Dim your adversary's Anima by 10." Ten Khao AND ten Anima — the
  // most expensive card in Terror, and a third of a starting Warlock's
  // life removed in one beat. "The First & Last, Zavixu the A'aeternis,
  // deemed these dragons Worthy."
  //
  // The species' Lyt Attack, The Decree, is already the one attack
  // aimed at the BOARD rather than at a card. Show Stomper is that
  // taken to its conclusion: a foot comes down. The rise before it is
  // the whole card — everything lifts, hangs, and then the ground on
  // the far side stops existing. Longest single silence in the file at
  // ~200ms, and it is doing more work than any cue around it.
  u_premier_dragon_lord: score("Show Stomper", [
    ...signature(P.roseSoft, P.dragon),
    spiral(280, P.crimson, { ...S, ms: 640, tone: "up", spin: 2.4, r: 0.9, size: 3.4 }),
    motes(340, P.scale, { ...S, ms: 560, n: 40, r: 2.4, size: 0.08, tone: "in" }),
    // ── the hang. Nothing fires between 900 and 1080. ──
    pillar(1080, P.dragon, { ms: 460, r: 6.4, size: 0.86, spin: 0 }),
    crack(1180, P.bloodDeep, { ms: 780, n: 12, r: 4.2 }),
    wave(1200, P.crimson, { from: "seam", to: "target", ms: 640, r: 7.4, size: 0.7, tone: 0.95 }),
    // THE FOOT IS THE TOLL. An earlier cut fired a full weight-3 wound
    // AND a shatter on top of the crack here, and the linter caught 7
    // things live at 1360ms — which is not a stomp, it is a blur. The
    // crack IS the world's acknowledgement; it does not need a ring
    // underneath it saying the same word. Wound dropped to weight 2 and
    // the shatter pushed out past the crack's own life, so the debris
    // reads as aftermath instead of as part of the impact.
    ...wound(P.crimson, 1220, 2),
    shatter(1620, P.roseSoft, { ms: 720, n: 16, r: 2.0 }),
    // The kick is timed to the FOOT (1080ms), not to the top of the
    // score. A table that jumped when the pillar started would be
    // shaking at nothing — the whole card is the hang and then the
    // landing, and the world may only answer the landing.
  ], 2200, { shake: SHAKE.world(1080) , surge: SURGE.total(P.crimson, 1080) }),

  // ── CHAOS DRAGON · I OR NONE ──────────────────────────────────────
  // "Warlocks must ruin their own beings until each has only 1 being
  // left." The most destructive card in the game and the only one that
  // dismantles BOTH boards — the caster's own included. "The One &
  // Only, Xavizu the A'oeternis, in a war with these dragons, admitted
  // that they are a Threat."
  //
  // Everything else in this file eventually points at the adversary.
  // This one points at the table. The crack opens under the SEAM, the
  // wave rolls outward in both directions from there, and the shatter
  // fires twice — once on each half. The player's own beings are in
  // that second one and the score does not spare them, because the card
  // doesn't.
  //
  // 2.4s — the longest score in Terror, and the only card that has
  // earned it.
  u_chaos_dragon_lord: score("I or None", [
    ...signature(P.crimson, P.bloodDeep),
    spiral(280, P.crimson, { ...S, ms: 700, tone: "up", spin: 3.4, r: 1.0, size: 3.8 }),
    embers(400, P.bloodDeep, { ...S, ms: 620, n: 34, r: 1.0, size: 0.085, size2: 2.0 }),
    crack(900, P.bloodDeep, { to: "seam", ms: 860, n: 14, r: 5.0 }),
    wave(960, P.crimson, { from: "seam", to: "target", ms: 700, r: 7.6, size: 0.72, tone: 0.95 }),
    wave(1000, P.crimson, { from: "seam", to: "self", ms: 700, r: 7.6, size: 0.72, tone: 0.95 }),
    // ── THE TWO HALVES COME APART ONE AT A TIME ──────────────────
    // The adversary's board breaks first and the caster's follows,
    // 340ms later. Firing them together was the denser, worse version
    // (the linter counted 7 live at 1560ms) and it also told the wrong
    // story: the whole horror of I or None is that you watch it come
    // back for YOUR side after it is done with theirs. The gap is the
    // card. The toll drops to weight 2 for the same reason it did on
    // Show Stomper — the sever already spoke.
    sever(1320, P.roseSoft, { to: "seam", ms: 680, r: 3.2, size: 0.06, size2: 0.44, tone: 0 }),
    shatter(1440, P.crimson, { ms: 760, n: 18, r: 2.2 }),
    ...toll(P.crimson, 1500, 2, "seam"),
    shatter(1780, P.bloodDeep, { ...S, ms: 760, n: 18, r: 2.2 }),
  ], 2400, { shake: SHAKE.world(900) , surge: SURGE.total(P.bloodDeep, 900) }),
};

// ── THE CODEX VIEW ─────────────────────────────────────────────────
export const describeUltis = () =>
  Object.keys(ULTIS).map((k) => ({
    channel: "U'HLTI", key: k, name: ULTIS[k].name, ms: ULTIS[k].ms,
    cues: ULTIS[k].cues.length,
  }));

export default ULTIS;
